/**
 * Remote calls and pure helpers for the Webflow Deployer admin tool. The endpoints live in
 * functions-22/src/webflowDeployer.
 */

import { IN_PROGRESS_STATUSES, WebflowDeployStatus } from '../Model/WebflowDeploy';
import { postJson, postRun } from './remoteError';

const LIST_NETLIFY_SITES_PATH = 'webflowDeployer-listNetlifySites';
const INSPECT_WEBFLOW_UPLOAD_PATH = 'webflowDeployer-inspectWebflowUpload';
const DEPLOY_WEBFLOW_SITE_PATH = 'webflowDeployer-deployWebflowSite';

/** Uploads live under this root, one per deploy: `webflowDeploys/{uid}/{runId}.zip`. */
const UPLOAD_ROOT = 'webflowDeploys';
const ZIP_CONTENT_TYPE = 'application/zip';

/**
 * The largest upload accepted, in MB (of 1024 * 1024 bytes) — checked here first so an oversized
 * ZIP is not uploaded only to fail. The server enforces the same limit in two places; change all
 * three together: MAX_UPLOAD_MB in system2-server functions-22/src/webflowDeployer/config.ts and the
 * `request.resource.size` check on webflowDeploys/ in system2-server storage.rules.
 */
const MAX_UPLOAD_MB = 250;
const MAX_UPLOAD_BYTES = MAX_UPLOAD_MB * 1024 * 1024;

/** Site picker value for "create a new Netlify site" rather than redeploying an existing one. */
const NEW_SITE_ID = '__NEW_SITE__';

/**
 * What the page shows for a deploy: its document's status with the running ones collapsed into
 * IN_PROGRESS, plus the two states a document cannot record — no document yet, and a running
 * document whose deadline has passed.
 */
const DeployDisplayStatus = {
  PENDING: 'PENDING',
  IN_PROGRESS: 'IN_PROGRESS',
  COMPLETED: WebflowDeployStatus.COMPLETED,
  FAILED: WebflowDeployStatus.FAILED,
  TIMED_OUT: 'TIMED_OUT',
};

/**
 * Lists the Netlify sites the deployer can target.
 * @param {Function} remote - firebase.remote.
 * @returns {Promise<Array>} Array of { id, name, url, customDomain }.
 */
const listNetlifySites = async (remote) => {
  const result = await postJson(remote, LIST_NETLIFY_SITES_PATH, {}, 'Failed to load the Netlify sites');
  return result?.sites || [];
};

/**
 * Validates an uploaded export and suggests where to deploy it.
 * @param {string} storagePath - The uploaded ZIP's storage path.
 * @param {Function} remote - firebase.remote.
 * @returns {Promise<Object>} { validation, webflowSiteId, suggestedSite, suggestedNewSiteName }.
 */
const inspectWebflowUpload = (storagePath, remote) => postJson(
  remote,
  INSPECT_WEBFLOW_UPLOAD_PATH,
  { storagePath },
  'Failed to inspect the uploaded export',
);

/**
 * Deploys an uploaded export and waits for the endpoint to finish it.
 *
 * A rejection does not always mean nothing happened: the request can die while the endpoint
 * keeps going, and the webflowDeploys/{runId} document is what settles that. `isRunRejected`
 * on the thrown error (see postRun) says whether there is a deploy to follow.
 *
 * @param {Object} request - The body built by buildDeployRequest.
 * @param {Function} remote - firebase.remote.
 * @returns {Promise<Object>} { runId, deployId, siteId, siteName, url }.
 */
const deployWebflowSite = (request, remote) => postRun(
  remote,
  DEPLOY_WEBFLOW_SITE_PATH,
  request,
  'Failed to deploy the site',
);

/**
 * Where an upload lands. The uid scopes it to its uploader, which storage.rules enforces.
 * @param {string} uid - The uploader's uid.
 * @param {string} runId - The reserved deploy id.
 * @returns {string} The storage path.
 */
const buildStoragePath = (uid, runId) => `${UPLOAD_ROOT}/${uid}/${runId}.zip`;

/**
 * Storage metadata for an upload. The original name rides along because the stored object is
 * named by runId, and the endpoint needs the name for history and filename-based site matching.
 * @param {string} fileName - The picked file's name.
 * @returns {Object} Upload metadata.
 */
const buildUploadMetadata = (fileName) => ({
  contentType: ZIP_CONTENT_TYPE,
  customMetadata: { originalName: fileName },
});

/**
 * Whether a picked file is a ZIP. By extension: browsers disagree on a ZIP's MIME type
 * (application/zip, application/x-zip-compressed, or empty), but a Webflow export is always .zip.
 * @param {File} file - The picked file.
 * @returns {boolean}
 */
const isZipFile = (file) => /\.zip$/i.test(file?.name || '');

/**
 * Whether a picked file fits the upload limit. Inclusive, as storage.rules is.
 * @param {File} file - The picked file.
 * @returns {boolean}
 */
const isWithinUploadLimit = (file) => file.size <= MAX_UPLOAD_BYTES;

/**
 * The deploy request for a file. Exactly one of siteId / newSiteName, as the endpoint requires.
 * @param {Object} params - The file's deploy target.
 * @param {string} params.storagePath - The uploaded ZIP's storage path.
 * @param {string} params.runId - The reserved deploy id.
 * @param {string} params.selectedSiteId - A Netlify site id, or NEW_SITE_ID.
 * @param {string} params.newSiteName - The name for a new site, used only with NEW_SITE_ID.
 * @returns {Object} The deployWebflowSite body.
 */
const buildDeployRequest = ({
  storagePath,
  runId,
  selectedSiteId,
  newSiteName,
}) => (selectedSiteId === NEW_SITE_ID
  ? { storagePath, runId, newSiteName: (newSiteName || '').trim() }
  : { storagePath, runId, siteId: selectedSiteId });

/**
 * Whether a file has a usable deploy target picked.
 * @param {string} selectedSiteId - A Netlify site id, NEW_SITE_ID, or empty.
 * @param {string} newSiteName - The name for a new site.
 * @returns {boolean}
 */
const hasDeployTarget = (selectedSiteId, newSiteName) => (selectedSiteId === NEW_SITE_ID
  ? !!(newSiteName || '').trim()
  : !!selectedSiteId);

/** Why a site is pre-selected for a file. */
const SuggestionReason = {
  /** The endpoint remembers this Webflow site being deployed there. */
  LAST_DEPLOYED: 'LAST_DEPLOYED',
  /** Nothing is remembered, but an existing site already has the name a new site would get. */
  SAME_NAME: 'SAME_NAME',
};

/**
 * The site to pre-select for an inspected file, if any. The endpoint's remembered site wins.
 * Without one, an existing site whose name is exactly the suggested new-site name is the same
 * site deployed before: deploying "as new" would only create a timestamped duplicate of it.
 * @param {Object} inspection - The inspectWebflowUpload response.
 * @param {Array} sites - The Netlify sites, { id, name, ... }.
 * @returns {Object|null} { id, name, reason } or null.
 */
const resolveSuggestedSite = (inspection, sites = []) => {
  if (inspection?.suggestedSite) {
    return { ...inspection.suggestedSite, reason: SuggestionReason.LAST_DEPLOYED };
  }
  const name = inspection?.suggestedNewSiteName;
  const match = name ? sites.find((site) => site.name === name) : undefined;
  return match ? { id: match.id, name: match.name, reason: SuggestionReason.SAME_NAME } : null;
};

/**
 * The deploy target a file starts with once inspected: the suggested site when there is one
 * (see resolveSuggestedSite), otherwise a new site under the suggested name.
 * @param {Object} inspection - The inspectWebflowUpload response.
 * @param {Array} [sites] - The Netlify sites loaded so far.
 * @returns {Object} { selectedSiteId, newSiteName }.
 */
const initialDeployTarget = (inspection, sites) => ({
  selectedSiteId: resolveSuggestedSite(inspection, sites)?.id || NEW_SITE_ID,
  newSiteName: inspection?.suggestedNewSiteName || '',
});

/**
 * The target a file deploys to: what the user picked or typed, falling back to the derived
 * pre-selection (initialDeployTarget) for a field still null. A queue item keeps both fields
 * null until the user's first edit, so a pre-selection follows sites that load late but never
 * overrides a choice.
 * @param {Object} item - { inspection, selectedSiteId: string|null, newSiteName: string|null }.
 * @param {Array} sites - The Netlify sites loaded so far.
 * @returns {Object} { selectedSiteId, newSiteName }.
 */
const effectiveDeployTarget = (item, sites) => {
  const initial = initialDeployTarget(item.inspection, sites);
  return {
    selectedSiteId: item.selectedSiteId ?? initial.selectedSiteId,
    newSiteName: item.newSiteName ?? initial.newSiteName,
  };
};

/**
 * Filters the site picker's sites by a search term, matched case-insensitively against the
 * site name, its Netlify URL and its custom domain. Order is preserved.
 * @param {Array} sites - Array of { id, name, url, customDomain }.
 * @param {string} query - The search term.
 * @returns {Array} The matching sites.
 */
const filterSites = (sites, query) => {
  const term = (query || '').trim().toLowerCase();
  if (!term) {
    return sites;
  }
  return sites.filter(({ name, url, customDomain }) => [name, url, customDomain]
    .some((field) => (field || '').toLowerCase().includes(term)));
};

/**
 * What a deploy document says, with the deadline applied: a document still running past
 * `deadlineAt` belongs to a deploy the platform killed, which never writes a final status.
 * @param {Object|null} deploy - { status, deadlineAt: Date|undefined }, or null for no document.
 * @param {number} now - Current time in ms.
 * @returns {string} A DeployDisplayStatus.
 */
const deriveDeployStatus = (deploy, now) => {
  if (!deploy?.status) {
    return DeployDisplayStatus.PENDING;
  }
  if (IN_PROGRESS_STATUSES.includes(deploy.status)) {
    const deadline = deploy.deadlineAt?.getTime();
    return deadline && now >= deadline ? DeployDisplayStatus.TIMED_OUT : DeployDisplayStatus.IN_PROGRESS;
  }
  return deploy.status;
};

export {
  NEW_SITE_ID,
  DeployDisplayStatus,
  listNetlifySites,
  inspectWebflowUpload,
  deployWebflowSite,
  buildStoragePath,
  buildUploadMetadata,
  isZipFile,
  isWithinUploadLimit,
  MAX_UPLOAD_MB,
  buildDeployRequest,
  hasDeployTarget,
  initialDeployTarget,
  effectiveDeployTarget,
  resolveSuggestedSite,
  SuggestionReason,
  filterSites,
  deriveDeployStatus,
};
